import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Calendar, MapPin, Users } from 'lucide-react';
import { fairApi } from '../services/api';
import { Fair } from '../types';
import SeekingManagerBadge from './SeekingManagerBadge';

interface CalendarDay {
  date: Date;
  isCurrentMonth: boolean;
  fairs: Fair[];
}

const CalendarView: React.FC = () => {
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [calendarDays, setCalendarDays] = useState<CalendarDay[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Get month start and end dates
  const getMonthRange = (date: Date): { start: string; end: string } => {
    const year = date.getFullYear();
    const month = date.getMonth();

    const startDate = new Date(year, month, 1);
    const endDate = new Date(year, month + 1, 0);

    return {
      start: startDate.toISOString().split('T')[0],
      end: endDate.toISOString().split('T')[0]
    };
  };

  // Generate calendar grid with padding for previous/next month
  const generateCalendarGrid = (date: Date, fairs: Fair[]): CalendarDay[] => {
    const year = date.getFullYear();
    const month = date.getMonth();

    // First day of month and last day of month
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    // Start from Sunday before first day
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - startDate.getDay());

    // End on Saturday after last day
    const endDate = new Date(lastDay);
    endDate.setDate(endDate.getDate() + (6 - endDate.getDay()));

    const days: CalendarDay[] = [];
    const currentDay = new Date(startDate);

    while (currentDay <= endDate) {
      const isCurrentMonth = currentDay.getMonth() === month;

      // Filter fairs that fall on this date
      // Compare date strings (YYYY-MM-DD) to avoid timezone issues
      const dayFairs = fairs.filter(fair => {
        if (!fair.start_date) return false;

        const currentDayStr = currentDay.toISOString().split('T')[0];
        const fairStartStr = fair.start_date;
        const fairEndStr = fair.end_date || fair.start_date;

        return currentDayStr >= fairStartStr && currentDayStr <= fairEndStr;
      });

      days.push({
        date: new Date(currentDay),
        isCurrentMonth,
        fairs: dayFairs
      });

      currentDay.setDate(currentDay.getDate() + 1);
    }

    return days;
  };

  // Fetch fairs for current month
  useEffect(() => {
    const fetchFairs = async () => {
      setLoading(true);
      setError(null);

      try {
        const { start, end } = getMonthRange(currentDate);
        const response = await fairApi.searchPaginated(
          undefined, // state
          undefined, // city
          undefined, // productCategory
          1, // page
          1000, // pageSize - get all fairs for the month
          start,
          end
        );

        const grid = generateCalendarGrid(currentDate, response.items);
        setCalendarDays(grid);
      } catch (err: unknown) {
        console.error('Failed to fetch fairs:', err);
        setError('Failed to load calendar events. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchFairs();
  }, [currentDate]);

  // Navigation handlers
  const goToPreviousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Format month/year display
  const monthYearDisplay = currentDate.toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric'
  });

  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const today = new Date().toISOString().split('T')[0];

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <Calendar className="h-8 w-8 text-secondary" />
              <h1 className="text-3xl font-bold text-gray-900">Event Calendar</h1>
            </div>
            <button
              onClick={goToToday}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
            >
              Today
            </button>
          </div>
          <p className="text-gray-600">
            Browse upcoming fairs and events. Click on any event to view details.
          </p>
        </div>

        {/* Calendar Controls */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="flex items-center justify-between">
            <button
              onClick={goToPreviousMonth}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Previous month"
            >
              <ChevronLeft className="h-6 w-6 text-gray-600" />
            </button>

            <h2 className="text-xl font-semibold text-gray-900">
              {monthYearDisplay}
            </h2>

            <button
              onClick={goToNextMonth}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Next month"
            >
              <ChevronRight className="h-6 w-6 text-gray-600" />
            </button>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            <p className="mt-4 text-gray-600">Loading calendar...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-800">{error}</p>
            <button
              onClick={() => setCurrentDate(new Date(currentDate))}
              className="mt-2 text-red-600 hover:text-red-800 font-medium"
            >
              Try Again
            </button>
          </div>
        )}

        {/* Calendar Grid */}
        {!loading && !error && (
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
            {/* Week Day Headers */}
            <div className="grid grid-cols-7 bg-gray-50 border-b border-gray-200">
              {weekDays.map((day) => (
                <div
                  key={day}
                  className="py-3 text-center text-sm font-semibold text-gray-700"
                >
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Days */}
            <div className="grid grid-cols-7 divide-x divide-y divide-gray-200">
              {calendarDays.map((day, index) => {
                const dateStr = day.date.toISOString().split('T')[0];
                const isToday = dateStr === today;

                return (
                  <div
                    key={index}
                    className={`min-h-32 p-2 ${
                      !day.isCurrentMonth ? 'bg-gray-50' : 'bg-white'
                    } hover:bg-gray-50 transition-colors`}
                  >
                    {/* Day Number */}
                    <div className="flex justify-between items-start mb-2">
                      <span
                        className={`text-sm font-medium ${
                          isToday
                            ? 'bg-primary text-white rounded-full w-6 h-6 flex items-center justify-center'
                            : day.isCurrentMonth
                            ? 'text-gray-900'
                            : 'text-gray-400'
                        }`}
                      >
                        {day.date.getDate()}
                      </span>
                    </div>

                    {/* Fair Events */}
                    <div className="space-y-1">
                      {day.fairs.slice(0, 3).map((fair) => (
                        <Link
                          key={fair.id}
                          to={`/fairs/${fair.id}`}
                          className="block group"
                        >
                          <div className="bg-teal-50 hover:bg-secondary-light/100 border border-teal-200 rounded p-1.5 transition-colors">
                            <div className="flex items-start justify-between">
                              <div className="flex-1 min-w-0">
                                <p className="text-xs font-medium text-teal-900 truncate group-hover:text-primary-dark">
                                  {fair.name}
                                </p>
                                {fair.submitted_by == null && (
                                  <div className="mt-1 overflow-hidden">
                                    <SeekingManagerBadge size="small" />
                                  </div>
                                )}
                                {fair.address?.city && (
                                  <div className="flex items-center mt-0.5 text-xs text-primary-dark">
                                    <MapPin className="h-3 w-3 mr-0.5 flex-shrink-0" />
                                    <span className="truncate">{fair.address.city}</span>
                                  </div>
                                )}
                              </div>

                              {/* Available Slots Badge */}
                              {fair.available_slots !== undefined && (
                                <div className="ml-1 flex-shrink-0">
                                  <div className="bg-primary text-white rounded px-1.5 py-0.5 text-xs font-semibold flex items-center">
                                    <Users className="h-3 w-3 mr-0.5" />
                                    {fair.available_slots}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </Link>
                      ))}

                      {/* More indicator */}
                      {day.fairs.length > 3 && (
                        <div className="text-xs text-gray-600 text-center py-1">
                          +{day.fairs.length - 3} more
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Legend */}
        <div className="mt-6 bg-white rounded-lg shadow-sm p-4">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Legend</h3>
          <div className="flex flex-wrap gap-4 text-sm text-gray-600">
            <div className="flex items-center">
              <div className="w-6 h-6 bg-primary rounded-full mr-2"></div>
              <span>Today</span>
            </div>
            <div className="flex items-center">
              <div className="w-6 h-6 bg-teal-50 border border-teal-200 rounded mr-2"></div>
              <span>Fair/Event</span>
            </div>
            <div className="flex items-center">
              <Users className="h-5 w-5 text-secondary mr-1" />
              <span>Available Vendor Slots</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CalendarView;
